import { PollDetails } from 'api/api.types';
import { getFormattedCandidates } from './getFormattedCandidates';
import { useGetFormattedLists } from './useGetFormattedLists';
import { useGetFormattedListGroups } from './useGetFormattedListGroups';
import { useGetFormattedParties } from './useGetFormattedParties';

const ELECTION_TERM_DURATION = 4;

const useGetProportionalElectionDetails = (
  details: PollDetails,
  previousElectionResults: PollDetails,
) => {
  const isFinal = details.progress.final;
  const currentYear = new Date(details.info.date).getFullYear();
  const prevYear =
    new Date(previousElectionResults?.info.date).getFullYear() ||
    currentYear - ELECTION_TERM_DURATION;

  const isBiProportional = details.info.type === 'election_biproportional_apportionment';

  // reduce details of lists, parties and candidates to the necessary information
  const formattedLists = useGetFormattedLists(
    details.lists,
    isFinal,
    details.progress?.number_of_finished_counting_circles,
  );
  const formattedParties = useGetFormattedParties(
    details.parties,
    formattedLists,
    isFinal,
    details.progress?.number_of_finished_counting_circles,
  );
  const formattedListGroups = useGetFormattedListGroups(
    details.list_groups,
    isFinal,
    details.progress?.number_of_finished_counting_circles,
  );

  const formattedCandidates = isBiProportional
    ? undefined
    : getFormattedCandidates(details.candidates, formattedLists, formattedParties);

  const listResultsWithPrevResults = formattedLists?.map((list) => {
    const prevList = previousElectionResults?.lists?.find(
      (l) => l.list_number === list.listNumber,
    );
    return {
      ...list,
      competedInPrevElection: Boolean(prevList),
      seatsClaimedPrev: prevList?.results?.seat_claim,
      votesAbsolutePrev: prevList?.results?.party_votes,
      votesRelativePrev: prevList?.results?.party_votes_percentage,
    };
  });

  const partyResultsWithPrevResults = formattedParties?.map((party) => {
    const prevList = previousElectionResults?.parties?.find((p) => p.id === party.listId);
    return {
      ...party,
      competedInPrevElection: Boolean(prevList),
      seatsClaimedPrev: prevList?.results?.seat_claim,
      votesAbsolutePrev: prevList?.results?.party_votes,
      votesRelativePrev: prevList?.results?.party_votes_percentage,
    };
  });

  const listGroupResultsWithPrevResults = formattedListGroups?.map((listgroup) => {
    const prevList = previousElectionResults?.list_groups?.find(
      (g) => g.party_id === listgroup.partyId,
    );
    return {
      seatsClaimed: 0,
      ...listgroup,
      competedInPrevElection: Boolean(prevList),
      seatsClaimedPrev: prevList?.results?.seat_claim,
      votesAbsolutePrev: parseFloat(prevList?.results?.voters_count.toFixed(2)),
      votesRelativePrev: prevList?.results?.voters_count_percentage,
    };
  });

  return {
    currentYear,
    prevYear,
    isBiProportional,
    isFinal,
    formattedListGroups,
    formattedParties,
    formattedCandidates,
    listResultsWithPrevResults,
    partyResultsWithPrevResults,
    listGroupResultsWithPrevResults,
  };
};

export default useGetProportionalElectionDetails;
